import { useMemo } from 'react';
import type { EChartsOption } from 'echarts';
import * as echarts from 'echarts';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { CensoredDataPoint } from '@/utils/weibullCensored';
import { ColorByOption, MarkerByOption, SizeByOption } from '@/components/analysis/PlotCustomization';

// Type for failure records in plot data (different from API FailureRecord)
type PlotFailureRecord = {
  case_id: string;
  turbine_id: string;
  site_name: string;
  component_major: string;
  component_minor: string;
  failure_mode: string;
  outcome_name: string;
  failure_date: string;
  operation_time_years: number;
  turbine_model: string;
  turbine_name: string;
};

// Define tooltip parameter types
interface TooltipParams {
  componentType: string;
  seriesName: string;
  value?: [number, number];
  data?: {
    record?: {
      case_id: string;
      turbine_id: string;
      site_name: string;
      component_major: string;
      component_minor?: string;
      failure_mode?: string;
      outcome_name: string;
      failure_date: string;
    };
    // Present when tie-grouping collapses several failures sharing an operation time into one point;
    // carries every grouped record so the tooltip can render per-case detail.
    records?: PlotFailureRecord[];
    originalTime?: number;
    originalProbability?: number;
    count?: number;
    symbolSize?: number;
  };
}

// Weibull probability transformation functions
const weibullY = (probability: number): number => {
  return Math.log(-Math.log(1 - probability));
};

const inverseWeibullY = (y: number): number => {
  return 1 - Math.exp(-Math.exp(y));
};

// A buffer value to add to the x axis.
const X_AXIS_BUFFER = 10;


// If the x axis for operation time (years) is below 30, add a buffer to it.
const setMaxAxis = (value: number): number => {
  return value > 30 ? value : value + X_AXIS_BUFFER;
}

// Color palettes for different categories
const COLOR_PALETTES = {
  default: ['#3b82f6'],
  category10: [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
    '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16'
  ],
  category20: [
    '#3b82f6', '#93c5fd', '#ef4444', '#fca5a5', '#10b981', '#86efac',
    '#f59e0b', '#fde047', '#8b5cf6', '#c4b5fd', '#ec4899', '#f9a8d4',
    '#14b8a6', '#5eead4', '#f97316', '#fdba74', '#06b6d4', '#67e8f9',
    '#84cc16', '#bef264'
  ],
  timePeriod: {
    gradient: ['#dbeafe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8'],
  }
};

// Marker shapes for different categories
const MARKER_SHAPES = ['circle', 'rect', 'triangle', 'diamond', 'pin'];

// Helper function to get size legend data based on size option
const getSizeLegendData = (sizeBy: SizeByOption) => {
  if (sizeBy === 'operationTime') {
    return [
      { name: 'Long Operation', icon: 'circle' },
      { name: 'Medium Operation', icon: 'circle' },
      { name: 'Short Operation', icon: 'circle' }
    ];
  } else if (sizeBy === 'recent') {
    return [
      { name: 'Recent (< 30 days)', icon: 'circle' },
      { name: '30-90 days', icon: 'circle' },
      { name: '90-365 days', icon: 'circle' },
      { name: 'Older (> 1 year)', icon: 'circle' }
    ];
  }
  return [];
};

// HTML-escape user-derived strings before they enter a tooltip innerHTML string (defence in depth —
// case ids / component names / failure modes originate from the database, not a trusted constant).
const esc = (value: string | number | undefined | null): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const TIE_TOOLTIP_CASE_CAP = 6;

// Render an outcome ("Type") value as the same colored pill the single-point failure tooltip uses,
// so grouped and individual tooltips speak the same visual language. Colours key off the outcome
// name exactly as the individual tooltip does (Replace -> red, Planned -> blue, else -> amber). The
// value is HTML-escaped because it originates from the database, not a trusted constant.
const outcomePill = (outcomeName: string): string => {
  const pillStyle = outcomeName.includes('Replace')
    ? 'background-color: #fee2e2; color: #dc2626;'
    : outcomeName.includes('Planned')
      ? 'background-color: #dbeafe; color: #1e40af;'
      : 'background-color: #fef3c7; color: #d97706;';
  return `<span style="display: inline-flex; padding: 4px 8px; font-size: 12px; font-weight: 600; border-radius: 9999px; ${pillStyle}">${esc(outcomeName)}</span>`;
};

// Render the tooltip for a tie-grouped failure point (count > 1). Preserves the per-failure detail
// of the single-point tooltip: a "N failures at T years" header, any field identical across the
// whole group shown once, fields that differ listed per case (keyed by case id + turbine), and —
// beyond a cap — a tidy summary of distinct-value counts plus the first few case ids.
const buildGroupedTooltip = (
  title: string,
  accentColor: string,
  time: number | undefined,
  records: PlotFailureRecord[],
  count: number
): string => {
  const distinct = (selector: (r: PlotFailureRecord) => string): string[] =>
    Array.from(new Set(records.map(selector).filter((v) => v && v.length > 0)));

  const sites = distinct((r) => r.site_name);
  const components = distinct((r) => r.component_major);
  const subComponents = distinct((r) => r.component_minor);
  const failureModes = distinct((r) => r.failure_mode);
  const outcomes = distinct((r) => r.outcome_name);
  const turbines = distinct((r) => r.turbine_name || r.turbine_id);

  const row = (label: string, value: string): string => `
    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
      <span style="color: #6b7280;">${esc(label)}:</span>
      <span style="font-weight: 500; margin-left: 16px; text-align: right;">${esc(value)}</span>
    </div>`;

  // Same layout as `row` but the value is already-trusted HTML (e.g. the escaped outcome pill), so it
  // is injected verbatim rather than re-escaped.
  const rowHtml = (label: string, valueHtml: string): string => `
    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
      <span style="color: #6b7280;">${esc(label)}:</span>
      <span style="margin-left: 16px; text-align: right;">${valueHtml}</span>
    </div>`;

  // Fields shared across the whole group are shown once, using the same terminology and styling as
  // the single-point failure tooltip ("Type" rendered as the colored outcome pill, etc.).
  const sharedRows: string[] = [];
  if (sites.length === 1) sharedRows.push(row('Site', sites[0]));
  if (components.length === 1) sharedRows.push(row('Component', components[0]));
  if (subComponents.length === 1) sharedRows.push(row('Sub-component', subComponents[0]));
  if (failureModes.length === 1) sharedRows.push(row('Failure Mode', failureModes[0]));
  if (outcomes.length === 1) sharedRows.push(rowHtml('Type', outcomePill(outcomes[0])));

  // Per-case detail (only the fields that DIFFER across the group), capped for tidiness. The cap
  // decision is driven by how many records we can actually show, not the raw failure count (they
  // match for real backend data, where every failure carries a record).
  let detail: string;
  if (records.length <= TIE_TOOLTIP_CASE_CAP) {
    // Each case is a stacked block: the case id on its own line, then a labeled key/value list — one
    // field per line, indented and grey-labeled — on the lines below. Every field is explicitly
    // labeled so a reader can tell what each value means, and each sits in its own block element so
    // long values wrap inside the tooltip's max-width instead of running together on one row.
    const caseField = (label: string, value: string): string => `
            <div style="word-break: break-word;">
              <span style="color: #6b7280;">${esc(label)}:</span>
              <span> ${esc(value)}</span>
            </div>`;
    // Same per-case line but the value is already-trusted HTML (the escaped outcome pill).
    const caseFieldHtml = (label: string, valueHtml: string): string => `
            <div style="word-break: break-word;">
              <span style="color: #6b7280;">${esc(label)}:</span>
              <span> ${valueHtml}</span>
            </div>`;
    detail = records
      .map((r) => {
        const fields: string[] = [];
        if (components.length > 1) fields.push(caseField('Component', r.component_major));
        if (subComponents.length > 1) fields.push(caseField('Sub-component', r.component_minor));
        if (failureModes.length > 1) fields.push(caseField('Failure Mode', r.failure_mode || 'Unknown'));
        if (outcomes.length > 1) fields.push(caseFieldHtml('Type', outcomePill(r.outcome_name)));
        if (turbines.length > 1) fields.push(caseField('Turbine', r.turbine_name || r.turbine_id));
        // Failure date is always useful, so it is shown per case even though it is not part of the
        // shared-vs-differing split above. Matches the individual tooltip's "Estimated Shutdown Date".
        if (r.failure_date)
          fields.push(caseField('Estimated Shutdown Date', new Date(r.failure_date).toLocaleDateString()));
        const detailBody =
          fields.length > 0 ? fields.join('') : `\n            <div style="color: #9ca3af;">—</div>`;
        const caseId = esc(r.case_id);
        return `
          <div style="margin-bottom: 6px;">
            <div data-case-id="${caseId}" style="color: #111827; font-weight: 500;">#${caseId}</div>
            <div data-case-detail="${caseId}" style="color: #6b7280; font-size: 12px; margin-left: 12px;">${detailBody}
            </div>
          </div>`;
      })
      .join('');
  } else {
    const summaryBits: string[] = [];
    if (components.length > 1) summaryBits.push(`${components.length} components`);
    if (failureModes.length > 1) summaryBits.push(`${failureModes.length} failure modes`);
    if (outcomes.length > 1) summaryBits.push(`${outcomes.length} outcomes`);
    if (turbines.length > 1) summaryBits.push(`${turbines.length} turbines`);
    const firstIds = records
      .slice(0, TIE_TOOLTIP_CASE_CAP)
      .map((r) => `#${esc(r.case_id)}`)
      .join(', ');
    const more = records.length - TIE_TOOLTIP_CASE_CAP;
    detail = `
      ${summaryBits.length > 0 ? `<div style="color: #6b7280; margin-bottom: 4px;">${esc(summaryBits.join(', '))}</div>` : ''}
      <div style="color: #111827;">${firstIds} <span style="color: #9ca3af;">+ ${more} more</span></div>`;
  }

  const timeText = time !== undefined ? `${time.toFixed(2)} years` : '';
  return `
    <div style="background: white; padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); max-width: 24rem;">
      <div style="border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 8px;">
        <p style="font-weight: 600; color: ${accentColor}; margin: 0;">${esc(count)} ${esc(title)} at ${esc(timeText)}</p>
      </div>
      <div style="font-size: 14px;">
        ${sharedRows.join('')}
        ${sharedRows.length > 0 ? '<div style="height: 6px;"></div>' : ''}
        ${detail}
      </div>
    </div>`;
};

// Render the tooltip for a tie-grouped ONYX failure point (count > 1). ONYX observations carry ONLY
// a component — they have no case id, site, turbine, failure mode or outcome — so reusing
// `buildGroupedTooltip` would emit empty `#` case blocks and blank shared rows. This dedicated card
// mirrors the shared shell + accent header ("{count} failures at {T} years") but its body is a
// single Component row listing the distinct component(s) in the group. All record-derived text is
// HTML-escaped with `esc()` (defence in depth against DOM XSS / OWASP A03 Injection).
const buildOnyxGroupedTooltip = (
  time: number | undefined,
  records: PlotFailureRecord[],
  count: number
): string => {
  const components = Array.from(
    new Set(records.map((r) => r.component_major).filter((v) => v && v.length > 0))
  );
  const componentText = components.map(esc).join(', ');
  const componentRow =
    componentText.length > 0
      ? `
      <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
        <span style="color: #6b7280;">Component:</span>
        <span style="font-weight: 500; margin-left: 16px; text-align: right;">${componentText}</span>
      </div>`
      : '';
  const timeText = time !== undefined ? `${time.toFixed(2)} years` : '';
  return `
    <div style="background: white; padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); max-width: 24rem;">
      <div style="border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 8px;">
        <p style="font-weight: 600; color: #4046CA; margin: 0;">${esc(count)} failures at ${esc(timeText)}</p>
      </div>
      <div style="font-size: 14px;">
        ${componentRow}
      </div>
    </div>`;
};

interface UseWeibullChartOptionsProps {
  analysis: WeibullAnalysisResult;
  showConfidenceIntervals: boolean;
  confidenceLevel: number;
  showCensoredData: boolean;
  censoredData?: CensoredDataPoint[];
  showOnyxData: boolean;
  onyxCensorData?: CensoredDataPoint[];
  chartType: 'probability' | 'cdf';
  colorBy?: ColorByOption;
  markerBy?: MarkerByOption;
  sizeBy?: SizeByOption;
  showLegend?: boolean;
  /**
   * Display-only tie handling. When true (default), failures sharing an EXACT operation time are
   * collapsed to one representative point (plotted at the MEAN of the tied plotting positions —
   * see ADR 0012) carrying the full grouped record list; when false they fan into the
   * per-failure stack the backend emits. Never sent to the backend (see ADR 0012).
   */
  groupTiedTimes?: boolean;
}

export const useWeibullChartOptions = ({
  analysis,
  showConfidenceIntervals,
  confidenceLevel: _confidenceLevel,
  showCensoredData,
  censoredData,
  showOnyxData,
  onyxCensorData,
  chartType,
  colorBy = 'none',
  markerBy = 'none',
  sizeBy = 'uniform',
  showLegend = false,
  groupTiedTimes = true,
}: UseWeibullChartOptionsProps) => {
  return useMemo<EChartsOption>(() => {
    const operationTimes = analysis.plotData.operationTimes;
    const censoredTimes = showCensoredData && censoredData ? censoredData.map((d) => d.time) : [];
    const onyxOperationTimes = showOnyxData ? analysis.onyxPlotData?.operationTimes : [];
    const onyxCensorTimes = showOnyxData && onyxCensorData ? onyxCensorData.map((d) => d.time) : [];
    const allTimes = [...operationTimes, ...censoredTimes, ...onyxOperationTimes, ...onyxCensorTimes].filter((t) => t > 0);

    const dataMin = allTimes.length > 0 ? Math.min(...allTimes) : 0.1;
    const dataMax = allTimes.length > 0 ? Math.max(...allTimes) : 10;

    const span = dataMax - dataMin;
    let interval: number;
    if (span <= 5) {
      interval = 1;
    } else if (span <= 10) {
      interval = 2;
    } else if (span <= 14) {
      interval = 2;
    } else {
      interval = 5;
    }

    const minBoundary = Math.floor(dataMin / interval) * interval;
    const maxBoundary = Math.ceil(dataMax / interval) * interval;

    const getNiceLogMin = (value: number): number => {
      const niceValues = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100];
      for (let i = niceValues.length - 1; i >= 0; i--) {
        if (niceValues[i] <= value) {
          return niceValues[i];
        }
      }
      return 0.1;
    };

    const logMinBoundary = getNiceLogMin(dataMin);

    // Helper function to get category value for a record
    const getCategoryValue = (record: PlotFailureRecord, category: ColorByOption | MarkerByOption): string => {
      if (!record) return 'Unknown';
      switch (category) {
        case 'site':
          return record.site_name || 'Unknown Site';
        case 'component':
          return record.component_major || 'Unknown Component';
        case 'subComponent':
          return record.component_minor || 'Unknown Sub-Component';
        case 'turbine':
          return record.turbine_model || 'Unknown Model';
        case 'outcome':
          return record.outcome_name || 'Unknown Outcome';
        case 'timePeriod': {
          const year = new Date(record.failure_date).getFullYear();
          if (year < 2020) return 'Before 2020';
          if (year < 2022) return '2020-2021';
          if (year < 2024) return '2022-2023';
          return '2024+';
        }
        default:
          return 'Default';
      }
    };

    // Get unique categories for color and marker mapping
    const getUniqueCategories = (option: ColorByOption | MarkerByOption): string[] => {
      if (option === 'none') return ['Default'];
      const categories = new Set<string>();
      analysis.plotData.failureRecords?.forEach(record => {
        categories.add(getCategoryValue(record, option));
      });
      const sorted = Array.from(categories).sort();
      if (sorted.length > 20) {
        return [...sorted.slice(0, 20), 'Other'];
      }
      return sorted;
    };

    // Create marker mapping
    const getMarker = (record: PlotFailureRecord): string => {
      if (markerBy === 'none') return 'circle';
      const category = getCategoryValue(record, markerBy);
      const index = markerCategories.indexOf(category);
      if (index === -1) return 'circle';
      return MARKER_SHAPES[index % MARKER_SHAPES.length];
    };

    // Create size mapping
    const getSize = (record: PlotFailureRecord, index: number): number => {
      const baseSize = 8;
      if (sizeBy === 'uniform') return baseSize;
      if (sizeBy === 'operationTime') {
        const time = operationTimes[index];
        const normalized = (time - dataMin) / (dataMax - dataMin);
        return baseSize + normalized * 8; // 8-16px range
      }
      if (sizeBy === 'recent') {
        const date = new Date(record.failure_date);
        const now = new Date();
        const daysAgo = (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
        if (daysAgo < 30) return 16;
        if (daysAgo < 90) return 12;
        if (daysAgo < 365) return 10;
        return 6;
      }
      return baseSize;
    };

    // Get unique categories for color and marker mapping - shared between CDF and probability
    const colorCategories = getUniqueCategories(colorBy);
    const markerCategories = getUniqueCategories(markerBy);

    // Shape of a single failure-scatter data point consumed by ECharts. Carries the original
    // (un-transformed) time/probability for the tooltip, plus — when tie-grouping collapses several
    // failures sharing an operation time into one point — the full list of grouped records and a
    // count so the grouped tooltip can show per-case detail.
    type ScatterPoint = {
      value: [number, number];
      record?: PlotFailureRecord;
      records?: PlotFailureRecord[];
      originalTime: number;
      originalProbability: number;
      count?: number;
      symbol: string;
      symbolSize: number;
      itemStyle: { color: string; borderColor: string; borderWidth: number; opacity?: number };
    };

    // Build the failure scatter for a single (non-categorised) series from the parallel
    // times/probabilities/records arrays the backend emits per failure.
    //
    // The backend always emits one (fanned) point per failure: ties on operation time climb in y as
    // each tied failure takes the next sequential rank. `groupTiedTimes` (display-only — see ADR
    // 0012) optionally collapses every group of failures that share an EXACT operation time into ONE
    // representative point. The representative y is the MEAN of the tied points' plotting positions,
    // and the point carries `records[]` (all grouped records) + `count` for a rich tooltip.
    //
    // `toY` maps a 0-1 probability to the chart's y coordinate (Weibull-paper space for the
    // probability chart, raw percent for the CDF chart) so the same grouping is shared by both
    // branches.
    const buildFailureScatter = (
      times: number[],
      probabilities: number[],
      records: (PlotFailureRecord | undefined)[],
      color: string,
      toY: (probability: number) => number,
      opacity?: number
    ): ScatterPoint[] => {
      const makePoint = (
        time: number,
        probability: number,
        record: PlotFailureRecord | undefined,
        recordsAtTime: PlotFailureRecord[],
        count: number,
        index: number
      ): ScatterPoint => ({
        value: [time, toY(probability)],
        record,
        records: recordsAtTime,
        originalTime: time,
        originalProbability: probability * 100,
        count,
        symbol: getMarker(record as PlotFailureRecord),
        symbolSize: getSize(record as PlotFailureRecord, index),
        itemStyle: { color, borderColor: color, borderWidth: 1, opacity },
      });

      if (!groupTiedTimes) {
        return times.map((time, i) =>
          makePoint(time, probabilities[i], records[i], records[i] ? [records[i]!] : [], 1, i)
        );
      }

      // Aggregate by exact operation-time value. Equal day-counts produce identical doubles, so the
      // float key is safe here; we preserve first-seen order for a stable, time-sorted scatter.
      // `count` tracks the number of tied FAILURES (not records) so a grouped point reports the true
      // multiplicity even in the (backend-impossible) case of a missing record payload.
      //
      // We accumulate a running `sumProbability` so the grouped point can plot at the MEAN of the
      // tied positions. `maxIndex` (the highest-probability tied entry) is kept only to pick a
      // representative record (marker shape/size + shared single-line tooltip fields) — the plotted
      // y no longer depends on which record is chosen.
      const groups = new Map<
        number,
        {
          sumProbability: number;
          maxProbability: number;
          maxIndex: number;
          count: number;
          records: PlotFailureRecord[];
        }
      >();
      const order: number[] = [];
      times.forEach((time, i) => {
        const probability = probabilities[i];
        const record = records[i];
        const existing = groups.get(time);
        if (!existing) {
          order.push(time);
          groups.set(time, {
            sumProbability: probability,
            maxProbability: probability,
            maxIndex: i,
            count: 1,
            records: record ? [record] : [],
          });
          return;
        }
        existing.count++;
        existing.sumProbability += probability;
        if (record) existing.records.push(record);
        if (probability > existing.maxProbability) {
          existing.maxProbability = probability;
          existing.maxIndex = i;
        }
      });

      return order.map((time) => {
        const group = groups.get(time)!;
        // Representative record = the one at the max plotting position (drives marker/size + the
        // single-line fields when the whole group shares them); the plotted y is independent of it.
        const representative = records[group.maxIndex];
        // Plot at the MEAN of the tied positions (ADR 0012).
        const meanProbability = group.sumProbability / group.count;
        return makePoint(
          time,
          meanProbability,
          representative,
          group.records,
          group.count,
          group.maxIndex
        );
      });
    };

    if (chartType === 'cdf') {
      const fittedCDF = analysis.plotData.fittedLine
        .filter((point) => point.x >= dataMin && point.x <= dataMax)
        .map((point) => [point.x, point.y * 100]);

      const series: echarts.SeriesOption[] = [];

      const needsCategorization = colorBy !== 'none' || markerBy !== 'none' || sizeBy !== 'uniform';

      if (needsCategorization) {
        const createSeriesForCategory = (
          categoryType: string,
          categories: string[],
          isSecondary: boolean = false
        ) => {
          const dataByCategory: Record<string, Array<{
            value: [number, number];
            record: PlotFailureRecord;
            originalTime: number;
            originalProbability: number;
            symbol: string;
            symbolSize: number;
            itemStyle: {
              color: string;
              borderColor: string;
              borderWidth: number;
              opacity?: number;
            };
          }>> = {};

          analysis.plotData.operationTimes.forEach((time, i) => {
            const record = analysis.plotData.failureRecords?.[i];

            let category: string;
            if (categoryType === 'size') {
              const size = getSize(record, i);
              if (size <= 8) category = 'Small';
              else if (size <= 12) category = 'Medium';
              else category = 'Large';
            } else {
              category = getCategoryValue(record, categoryType as ColorByOption | MarkerByOption);
            }

            const limitedCategory = categories.includes(category) ? category : 'Other';

            if (!dataByCategory[limitedCategory]) {
              dataByCategory[limitedCategory] = [];
            }

            let dataPointColor: string;
            if (categoryType === colorBy || (isSecondary && colorBy !== 'none')) {
              const actualColorCategory = getCategoryValue(record, colorBy);
              const colorCategoryIndex = colorCategories.indexOf(actualColorCategory);
              dataPointColor = COLOR_PALETTES.category20[colorCategoryIndex % COLOR_PALETTES.category20.length];
            } else {
              dataPointColor = '#3b82f6';
            }

            const opacity = isSecondary ? 0.6 : 1;

            dataByCategory[limitedCategory].push({
              value: [time, analysis.plotData.probabilities[i] * 100],
              record,
              originalTime: time,
              originalProbability: analysis.plotData.probabilities[i] * 100,
              symbol: getMarker(record),
              symbolSize: getSize(record, i),
              itemStyle: {
                color: dataPointColor,
                borderColor: dataPointColor,
                borderWidth: 1,
                opacity: opacity
              }
            });
          });

          categories.forEach((category, index) => {
            const categoryData = dataByCategory[category] || [];
            if (categoryData.length > 0) {
              const seriesName = category;

              const seriesConfig: echarts.SeriesOption = {
                name: seriesName,
                type: 'scatter',
                data: categoryData,
                emphasis: {
                  scale: 1.5,
                },
              };

              if (isSecondary) {
                const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
                seriesConfig.color = color;
                seriesConfig.itemStyle = {
                  color: color,
                  borderColor: color,
                  borderWidth: 1,
                  opacity: 0.6
                };
                seriesConfig.symbol = categoryType === markerBy ?
                  MARKER_SHAPES[index % MARKER_SHAPES.length] : 'circle';
                seriesConfig.symbolSize = 6;
              } else {
                if (categoryType === colorBy) {
                  const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
                  seriesConfig.color = color;
                  seriesConfig.itemStyle = {
                    color: color,
                    borderColor: color,
                    borderWidth: 1
                  };
                } else {
                  seriesConfig.color = '#3b82f6';
                  seriesConfig.itemStyle = {
                    color: '#3b82f6',
                    borderColor: '#3b82f6',
                    borderWidth: 1
                  };
                }

                if (categoryType === markerBy) {
                  seriesConfig.symbol = MARKER_SHAPES[index % MARKER_SHAPES.length];
                }
              }

              series.push(seriesConfig);
            }
          });
        };

        if (colorBy !== 'none') {
          createSeriesForCategory(colorBy, colorCategories, false);
        } else if (markerBy !== 'none') {
          createSeriesForCategory(markerBy, markerCategories, false);
        } else if (sizeBy !== 'uniform') {
          createSeriesForCategory('size', ['Small', 'Medium', 'Large'], false);
        }

        if (colorBy !== 'none' && markerBy !== 'none' && markerBy !== colorBy) {
          createSeriesForCategory(markerBy, markerCategories, true);
        }

        if ((colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform') {
          createSeriesForCategory('size', ['Small', 'Medium', 'Large'], true);
        }
      } else {
        const cdfData = buildFailureScatter(
          analysis.plotData.operationTimes,
          analysis.plotData.probabilities,
          analysis.plotData.failureRecords ?? [],
          '#3b82f6',
          (p) => p * 100
        );

        series.push({
          name: 'Empirical CDF',
          type: 'scatter',
          color: '#3b82f6',
          data: cdfData,
          itemStyle: {
            color: '#3b82f6',
            borderColor: '#3b82f6',
            borderWidth: 1
          },
          emphasis: {
            scale: 1.5,
          },
        });
      }

      series.push({
        name: 'Fitted Weibull CDF',
        type: 'line',
        data: fittedCDF,
        smooth: false,
        showSymbol: false,
        lineStyle: {
          color: '#ef4444',
          width: 2,
        },
      });

      if (showConfidenceIntervals && analysis.plotData.confidenceBands) {
        const lowerBounds = analysis.plotData.confidenceBands.lower
          .filter((p) => p.x >= dataMin && p.x <= dataMax)
          .map((p) => [p.x, p.y * 100]);
        const upperBounds = analysis.plotData.confidenceBands.upper
          .filter((p) => p.x >= dataMin && p.x <= dataMax)
          .map((p) => [p.x, p.y * 100]);

        if (lowerBounds.length > 0 && upperBounds.length > 0) {
          const confidenceArea = [
            ...lowerBounds,
            ...upperBounds.reverse()
          ];

          series.push({
            name: 'Confidence Band',
            type: 'line',
            data: confidenceArea,
            showSymbol: false,
            lineStyle: {
              opacity: 0,
            },
            areaStyle: {
              color: 'rgba(59, 130, 246, 0.15)',
            },
            silent: true,
            z: 1, // Below other series
            tooltip: {
              show: false, // Don't show tooltip for confidence band
            },
          });

          // Add optional boundary lines for better visibility
          series.push(
            {
              name: 'Lower CI',
              type: 'line',
              data: lowerBounds,
              showSymbol: false,
              lineStyle: {
                color: 'rgba(59, 130, 246, 0.3)',
                width: 1,
                type: 'dashed',
              },
              silent: true,
              z: 2,
              tooltip: {
                show: false,
              },
            },
            {
              name: 'Upper CI',
              type: 'line',
              data: upperBounds.reverse(), // Reverse back to original order
              showSymbol: false,
              lineStyle: {
                color: 'rgba(59, 130, 246, 0.3)',
                width: 1,
                type: 'dashed',
              },
              silent: true,
              z: 2,
              tooltip: {
                show: false,
              },
            }
          );
        }
      }

      // Calculate dynamic padding based on legend positioning
      const baseBottomPadding = 60;
      const bottomPadding = baseBottomPadding + 30; // Space for primary legend at bottom
      // Calculate right padding based on secondary/tertiary legends on the right
      let rightPadding = 80; // Base padding
      if (showLegend && needsCategorization) {
        // Check if legends will be combined (same categorization for color and marker)
        const shouldCombineLegends = colorBy !== 'none' && markerBy !== 'none' && colorBy === markerBy;

        // Only add extra padding when secondary/tertiary legends will actually be on the right
        const hasSecondaryLegend = colorBy !== 'none' && markerBy !== 'none' && !shouldCombineLegends;
        const hasTertiaryLegend = (colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform';

        if (hasSecondaryLegend || hasTertiaryLegend) {
          rightPadding = 160; // Extra space for right-positioned legends
        }
      } else if (showLegend) {
        rightPadding = 80; // Basic padding for simple legends
      } else {
        rightPadding = 20; // Minimal padding when no legend
      }

      const option: EChartsOption = {
        grid: {
          left: 60,
          right: rightPadding,
          top: 35,
          bottom: bottomPadding,
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          name: 'Operation Time (years)',
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: {
            fontSize: 11,
            color: '#374151',
          },
          min: minBoundary,
          max: setMaxAxis(maxBoundary),
          axisLabel: {
            formatter: (value: number) => {
              if (value < 1) return value.toFixed(2);
              if (value < 10) return value.toFixed(1);
              return Math.round(value).toString();
            },
            fontSize: 10,
            color: '#6b7280',
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#e0e7ff',
              type: 'dashed',
              width: 1,
            },
          },
        },
        yAxis: {
          type: 'value',
          name: 'Cumulative Failure Probability (%)',
          nameLocation: 'middle',
          nameGap: 45,
          min: 0,
          max: 100,
        },
        tooltip: {
          trigger: 'item',
          // Keep the tooltip inside the chart viewport so points high on the y-axis don't render a
          // card that overflows the top of the plot and gets clipped by the ancestor
          // `overflow-y-auto` scroll container.
          confine: true,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#d1d5db',
          borderWidth: 1,
          borderRadius: 8,
          padding: 0,
          formatter: (params: unknown) => {
            const tooltipParams = params as TooltipParams;
            if (tooltipParams.componentType === 'series' &&
              (tooltipParams.seriesName === 'Empirical CDF' ||
                (needsCategorization && tooltipParams.seriesName !== 'Fitted Weibull CDF' && tooltipParams.seriesName !== 'Confidence Band'))) {
              const groupedRecords = tooltipParams.data?.records;
              if ((tooltipParams.data?.count ?? 1) > 1 && groupedRecords && groupedRecords.length > 1) {
                return buildGroupedTooltip('failures', '#3b82f6', tooltipParams.data?.originalTime, groupedRecords, tooltipParams.data?.count ?? groupedRecords.length);
              }
              const record = tooltipParams.data?.record;
              if (!record) return null;

              return `
                <div style="background: white; padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); max-width: 24rem;">
                  <div style="border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 8px;">
                    <p style="font-weight: 600; color: #111827; margin: 0;">Case #${esc(record.case_id)}</p>
                    <p style="font-size: 14px; color: #6b7280; margin: 4px 0 0 0;">${esc(record.turbine_id)} • ${esc(record.site_name)}</p>
                  </div>
                  <div style="font-size: 14px;">
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Operation Time:</span>
                      <span style="font-weight: 500;">${tooltipParams.data?.originalTime?.toFixed(2)} years</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Cumulative Probability:</span>
                      <span style="font-weight: 500; color: #3b82f6;">${tooltipParams.data?.originalProbability?.toFixed(1)}%</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Component:</span>
                      <span style="font-weight: 500;">${esc(record.component_major)}</span>
                    </div>
                    ${record.component_minor
                  ? `
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Sub-component:</span>
                      <span style="font-weight: 500;">${esc(record.component_minor)}</span>
                    </div>
                    `
                  : ''
                }
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Failure Mode:</span>
                      <span style="font-weight: 500;">${esc(record.failure_mode || 'Unknown')}</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Type:</span>
                      <span style="
                        display: inline-flex;
                        padding: 4px 8px;
                        font-size: 12px;
                        font-weight: 600;
                        border-radius: 9999px;
                        ${record.outcome_name.includes('Replace')
                  ? 'background-color: #fee2e2; color: #dc2626;'
                  : record.outcome_name.includes('Planned')
                    ? 'background-color: #dbeafe; color: #1e40af;'
                    : 'background-color: #fef3c7; color: #d97706;'
                }
                      ">
                        ${esc(record.outcome_name)}
                      </span>
                    </div>
                    <div style="display: flex; justify-content: space-between;">
                      <span style="color: #6b7280;">Estimated Shutdown Date:</span>
                      <span style="font-weight: 500;">${new Date(record.failure_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              `;
            }

            // Censored data tooltip
            if (tooltipParams.seriesName === 'Censored') {
              const count = tooltipParams.data?.count || 1;
              const turbineText = count === 1 ? 'turbine' : 'turbines';

              return `
                <div style="background: white; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);">
                  <div style="font-size: 14px;">
                    <div style="font-weight: 600; color: #111827; margin-bottom: 4px;">Censored Observations</div>
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Operation Time:</span>
                      <span style="font-weight: 500; margin-left: 16px;">${tooltipParams.data?.originalTime?.toFixed(2)} years</span>
                    </div>
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: #6b7280;">Number of ${turbineText}:</span>
                      <span style="font-weight: 500; margin-left: 16px;">${count}</span>
                    </div>
                    <div style="font-size: 12px; color: #9ca3af; margin-top: 4px;">Still operational</div>
                  </div>
                </div>
              `;
            }

            return null;
          },
        },
        toolbox: {
          feature: {
            dataZoom: {
              yAxisIndex: false,
              title: {
                zoom: 'Zoom',
                back: 'Reset',
              },
            },
            restore: {
              title: 'Reset',
            },
            saveAsImage: {
              title: 'Save',
              pixelRatio: 2,
            },
          },
          right: rightPadding,
          top: 10,
        },
        series,
      };

      // Add legend if needed
      if (showLegend) {
        if (needsCategorization) {
          // Create multiple legends with flexible positioning
          const legends = [];

          // Check if colorBy and markerBy are the same to combine legends
          const shouldCombineLegends = colorBy !== 'none' && markerBy !== 'none' && colorBy === markerBy;

          // Primary Legend: Show main categorization series (positioned below chart)
          if (colorBy !== 'none') {
            // Color-based primary series
            const primaryLegendData = colorCategories.map((category, index) => {
              const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
              return {
                name: category, // This matches the series name exactly
                icon: shouldCombineLegends ? MARKER_SHAPES[index % MARKER_SHAPES.length] : 'circle',
                itemStyle: {
                  color: color,
                  borderColor: color,
                  borderWidth: 1
                },
                textStyle: { color: '#374151', fontWeight: 'normal', lineHeight: 20, padding: [2, 0, 2, 4] }
              };
            });

            legends.push({
              data: primaryLegendData,
              type: 'scroll',
              orient: 'horizontal',
              bottom: 5,
              left: 'center',
              selector: false,
              itemWidth: 18,
              itemHeight: 14,
              itemGap: 15,
              scrollDataIndex: 0,
              pageButtonItemGap: 5,
              pageButtonGap: 10,
              pageIconSize: 12,
              pageTextStyle: { fontSize: 10 }
            });
          } else if (markerBy !== 'none') {
            // Marker-based primary series
            const primaryLegendData = markerCategories.map((category, index) => ({
              name: category, // This matches the series name exactly
              icon: MARKER_SHAPES[index % MARKER_SHAPES.length],
              textStyle: { color: '#374151', fontWeight: 'normal' }
            }));

            legends.push({
              data: primaryLegendData,
              type: 'scroll',
              orient: 'horizontal',
              bottom: 5,
              left: 'center',
              selector: false,
              itemWidth: 18,
              itemHeight: 14,
              itemGap: 15,
              scrollDataIndex: 0,
              pageButtonItemGap: 5,
              pageButtonGap: 10,
              pageIconSize: 12,
              pageTextStyle: { fontSize: 10 }
            });
          } else if (sizeBy !== 'uniform') {
            // Size-based primary series
            const sizeData = getSizeLegendData(sizeBy);
            const primaryLegendData = sizeData.map(item => ({
              name: item.name, // This matches the series name exactly
              icon: 'circle',
              textStyle: { color: '#374151', fontWeight: 'normal' }
            }));

            legends.push({
              data: primaryLegendData,
              type: 'scroll',
              orient: 'horizontal',
              bottom: 5,
              left: 'center',
              selector: false,
              itemWidth: 18,
              itemHeight: 14,
              itemGap: 15,
              scrollDataIndex: 0,
              pageButtonItemGap: 5,
              pageButtonGap: 10,
              pageIconSize: 12,
              pageTextStyle: { fontSize: 10 }
            });
          }

          // Secondary Legend: Show secondary categorization series (positioned on the right)
          if (colorBy !== 'none' && markerBy !== 'none' && !shouldCombineLegends) {
            const secondaryLegendData = markerCategories.map((category, index) => ({
              name: category, // Remove prefix like "Component:", "Turbine:", etc.
              icon: MARKER_SHAPES[index % MARKER_SHAPES.length],
              itemStyle: {
                color: '#6b7280',
                borderColor: '#6b7280',
                borderWidth: 1
              }, // Single color for all marker legend items
              textStyle: { color: '#6b7280', fontSize: 11, fontWeight: 'normal' }
            }));

            legends.push({
              data: secondaryLegendData,
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: '20%',
              selector: false,
              itemWidth: 16,
              itemHeight: 12,
              itemGap: 8,
              scrollDataIndex: 0,
              pageButtonItemGap: 4,
              pageButtonGap: 8,
              pageIconSize: 10,
              pageTextStyle: { fontSize: 9 },
              textStyle: { fontSize: 11, color: '#6b7280' }
            });
          }

          // Tertiary Legend: Show size categorization series when it's secondary (positioned on the right, below secondary)
          if ((colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform') {
            const sizeData = getSizeLegendData(sizeBy);
            const tertiaryLegendData = sizeData.map(item => ({
              name: item.name, // Remove prefix like "Size:"
              icon: 'circle',
              itemStyle: {
                color: '#9ca3af',
                borderColor: '#9ca3af',
                borderWidth: 1
              }, // Single color for all size legend items
              textStyle: { color: '#6b7280', fontSize: 10, fontWeight: 'normal' }
            }));

            legends.push({
              data: tertiaryLegendData,
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: '60%',
              selector: false,
              itemWidth: 14,
              itemHeight: 10,
              itemGap: 6,
              scrollDataIndex: 0,
              pageButtonItemGap: 3,
              pageButtonGap: 6,
              pageIconSize: 8,
              pageTextStyle: { fontSize: 8 },
              textStyle: { fontSize: 10, color: '#6b7280' }
            });
          }

          option.legend = legends;
        } else {
          // Basic series legend
          const seriesNames = series
            .filter(s => s.name && !['Lower CI', 'Upper CI', 'Confidence Band'].includes(s.name as string))
            .map(s => s.name as string);

          option.legend = {
            type: 'scroll',
            orient: 'horizontal',
            bottom: 5,
            left: 'center',
            itemGap: 15,
            data: seriesNames,
          };
        }
      }

      return option;
    }

    // Enhanced Probability Plot
    const fittedLine = analysis.plotData.fittedLine
      .filter((point) => point.x >= dataMin && point.x <= dataMax)
      .map((point) => [point.x, weibullY(point.y)]);

    const probTicks = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 30, 50, 70, 80, 90, 95, 99, 99.99];
    const customYValues = probTicks.map((prob) => weibullY(prob / 100));

    const series: echarts.SeriesOption[] = [];

    // Determine if we need to create categorized series (same logic as CDF)
    const needsCategorization = colorBy !== 'none' || markerBy !== 'none' || sizeBy !== 'uniform';

    if (needsCategorization) {
      // Helper function to create series for a specific categorization (reuse from CDF logic)
      const createSeriesForCategory = (
        categoryType: string,
        categories: string[],
        isSecondary: boolean = false
      ) => {
        const dataByCategory: Record<string, Array<{
          value: [number, number];
          record: PlotFailureRecord;
          originalTime: number;
          originalProbability: number;
          symbol: string;
          symbolSize: number;
          itemStyle: {
            color: string;
            borderColor: string;
            borderWidth: number;
            opacity?: number;
          };
        }>> = {};

        analysis.plotData.operationTimes.forEach((time, i) => {
          const record = analysis.plotData.failureRecords?.[i];

          let category: string;
          if (categoryType === 'size') {
            const size = getSize(record, i);
            if (size <= 8) category = 'Small';
            else if (size <= 12) category = 'Medium';
            else category = 'Large';
          } else {
            category = getCategoryValue(record, categoryType as ColorByOption | MarkerByOption);
          }

          const limitedCategory = categories.includes(category) ? category : 'Other';

          if (!dataByCategory[limitedCategory]) {
            dataByCategory[limitedCategory] = [];
          }

          // Determine color for this data point based on category
          let dataPointColor: string;
          if (categoryType === colorBy || (isSecondary && colorBy !== 'none')) {
            // Use color categorization when active OR when this is secondary series
            const actualColorCategory = getCategoryValue(record, colorBy);
            const colorCategoryIndex = colorCategories.indexOf(actualColorCategory);
            dataPointColor = COLOR_PALETTES.category20[colorCategoryIndex % COLOR_PALETTES.category20.length];
          } else {
            dataPointColor = '#3b82f6';
          }

          const opacity = isSecondary ? 0.6 : 1;

          dataByCategory[limitedCategory].push({
            value: [time, weibullY(analysis.plotData.probabilities[i])],
            record,
            originalTime: time,
            originalProbability: analysis.plotData.probabilities[i] * 100,
            symbol: getMarker(record),
            symbolSize: getSize(record, i),
            itemStyle: {
              color: dataPointColor,
              borderColor: dataPointColor,
              borderWidth: 1,
              opacity: opacity
            }
          });
        });

        // Create series for each category
        categories.forEach((category, index) => {
          const categoryData = dataByCategory[category] || [];
          if (categoryData.length > 0) {
            const seriesName = category; // Use clean names without prefixes for both primary and secondary

            const seriesConfig: echarts.SeriesOption = {
              name: seriesName,
              type: 'scatter',
              data: categoryData,
              emphasis: {
                scale: 1.5,
              },
            };

            // Apply styling based on whether this is primary or secondary
            if (isSecondary) {
              // Secondary series styling - more subtle
              const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
              seriesConfig.color = color; // Series-level color for ECharts fill
              seriesConfig.itemStyle = {
                color: color,
                borderColor: color,
                borderWidth: 1,
                opacity: 0.6
              };
              seriesConfig.symbol = categoryType === markerBy ?
                MARKER_SHAPES[index % MARKER_SHAPES.length] : 'circle';
              seriesConfig.symbolSize = 6; // Smaller for secondary
            } else {
              // Primary series styling - full visibility
              if (categoryType === colorBy) {
                const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
                seriesConfig.color = color;
                seriesConfig.itemStyle = {
                  color: color,
                  borderColor: color,
                  borderWidth: 1
                };
              } else {
                seriesConfig.color = '#3b82f6'; // Series-level color for ECharts fill
                seriesConfig.itemStyle = {
                  color: '#3b82f6',
                  borderColor: '#3b82f6',
                  borderWidth: 1
                };
              }

              if (categoryType === markerBy) {
                seriesConfig.symbol = MARKER_SHAPES[index % MARKER_SHAPES.length];
              }
            }

            series.push(seriesConfig);
          }
        });
      };

      // Create primary series (first active categorization)
      if (colorBy !== 'none') {
        createSeriesForCategory(colorBy, colorCategories, false);
      } else if (markerBy !== 'none') {
        createSeriesForCategory(markerBy, markerCategories, false);
      } else if (sizeBy !== 'uniform') {
        createSeriesForCategory('size', ['Small', 'Medium', 'Large'], false);
      }

      // Create secondary series (additional categorizations)
      if (colorBy !== 'none' && markerBy !== 'none' && markerBy !== colorBy) {
        createSeriesForCategory(markerBy, markerCategories, true);
      }

      if ((colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform') {
        createSeriesForCategory('size', ['Small', 'Medium', 'Large'], true);
      }
    } else {
      // Single series with default styling
      const failureData = buildFailureScatter(
        analysis.plotData.operationTimes,
        analysis.plotData.probabilities,
        analysis.plotData.failureRecords ?? [],
        '#3b82f6',
        weibullY
      );

      series.push({
        name: 'Failure Data',
        type: 'scatter',
        color: '#3b82f6', // Series-level color for ECharts fill
        data: failureData,
        itemStyle: {
          color: '#3b82f6',
          borderColor: '#3b82f6',
          borderWidth: 1
        },
        emphasis: {
          scale: 1.5,
        },
        z: 100,
      });
    }

    // Add fitted line
    series.push({
      name: 'Weibull Fit',
      type: 'line',
      data: fittedLine,
      smooth: false,
      showSymbol: false,
      lineStyle: {
        color: '#ef4444',
        width: 2,
      },
      silent: true,
    });

    // Add shaded confidence intervals using a simpler, more reliable approach
    if (showConfidenceIntervals && analysis.plotData.confidenceBands) {
      const lowerBounds = analysis.plotData.confidenceBands.lower
        .filter((p) => p.x >= dataMin && p.x <= dataMax)
        .map((p) => [p.x, weibullY(p.y)]);
      const upperBounds = analysis.plotData.confidenceBands.upper
        .filter((p) => p.x >= dataMin && p.x <= dataMax)
        .map((p) => [p.x, weibullY(p.y)]);

      if (lowerBounds.length > 0 && upperBounds.length > 0) {
        // Create confidence band as an area series
        // Combine lower and upper bounds to create a closed polygon
        const confidenceArea = [
          ...lowerBounds,
          ...upperBounds.reverse() // Reverse to close the polygon
        ];

        series.push({
          name: 'Confidence Band',
          type: 'line',
          data: confidenceArea,
          showSymbol: false,
          lineStyle: {
            opacity: 0,
          },
          areaStyle: {
            color: 'rgba(59, 130, 246, 0.15)',
          },
          silent: true,
          z: 1, // Below other series
          tooltip: {
            show: false, // Don't show tooltip for confidence band
          },
        });

        // Add optional boundary lines for better visibility
        series.push(
          {
            name: 'Lower CI',
            type: 'line',
            data: lowerBounds,
            showSymbol: false,
            lineStyle: {
              color: 'rgba(59, 130, 246, 0.3)',
              width: 1,
              type: 'dashed',
            },
            silent: true,
            z: 2,
            tooltip: {
              show: false,
            },
          },
          {
            name: 'Upper CI',
            type: 'line',
            data: upperBounds.reverse(), // Reverse back to original order
            showSymbol: false,
            lineStyle: {
              color: 'rgba(59, 130, 246, 0.3)',
              width: 1,
              type: 'dashed',
            },
            silent: true,
            z: 2,
            tooltip: {
              show: false,
            },
          }
        );
      }
    }

    // Add censored data if available
    if (showCensoredData && censoredData && censoredData.length > 0) {
      const censoredByTime: Record<number, number> = {};
      censoredData.forEach((point) => {
        const time = point.time;
        if (!censoredByTime[time]) censoredByTime[time] = 0;
        censoredByTime[time]++;
      });

      const censoredY = weibullY(0.0015);
      const censoredPoints = Object.entries(censoredByTime).map(([timeStr, count]) => ({
        value: [parseFloat(timeStr), censoredY],
        originalTime: parseFloat(timeStr),
        count: count,
        symbolSize: count === 1 ? 4 : count <= 10 ? 6 : count <= 50 ? 8 : 10,
        itemStyle: {
          color: '#e5e2d8',
          borderColor: '#e5e2d8',
          borderWidth: 1,
          opacity: 0.8
        }
      }));

      series.push({
        name: 'Censored',
        type: 'scatter',
        color: '#e5e2d8', // Series-level color for ECharts fill
        data: censoredPoints,
        symbolSize: (data: { symbolSize: number }) => data.symbolSize,
        itemStyle: {
          color: '#e5e2d8',
          borderColor: '#e5e2d8',
          borderWidth: 1,
          opacity: 0.8,
        },
        z: 3,
      });
    }

    // Add ONYX Data if available
    if (showOnyxData && onyxCensorData?.length > 0) {

      // Need to split this into censor and failure for onyx. The ONYX failure scatter honours the
      // same tie-grouping as the customer scatter (shared pre-step — see ADR 0012).
      const onyxFailureData = buildFailureScatter(
        analysis.onyxPlotData?.operationTimes ?? [],
        analysis.onyxPlotData?.probabilities ?? [],
        analysis.onyxPlotData?.failureRecords ?? [],
        '#4046CA',
        weibullY,
        0.5
      );

      series.push({
        name: 'ONYX Failure',
        type: 'scatter',
        color: '#4046CA', // Series-level color for ECharts fill
        data: onyxFailureData,
        itemStyle: {
          color: '#4046CA',
          borderColor: '#4046CA',
          borderWidth: 1,
        },
        emphasis: {
          scale: 0.5,
        },
        z: 4
      });

      if (showCensoredData) {
        const plotByTime: Record<number, number> = {};
        onyxCensorData.forEach((point) => {
          if (point.isCensored) {
            const time = point.time;
            if (!plotByTime[time]) plotByTime[time] = 0;
            plotByTime[time]++;
          }
        });

        const plotY = weibullY(0.0025);
        const points = Object.entries(plotByTime).map(([timeStr, count]) => ({
          value: [parseFloat(timeStr), plotY],
          originalTime: parseFloat(timeStr),
          count: count,
          symbolSize: count === 1 ? 4 : count <= 10 ? 6 : count <= 50 ? 8 : 10,
          itemStyle: {
            color: '#4046CA',
            borderColor: '#4046CA',
            borderWidth: 1,
            opacity: 0.8
          }
        }));

        series.push({
          name: 'ONYX Censor',
          type: 'scatter',
          color: '#4046CA', // Series-level color for ECharts fill
          data: points,
          symbolSize: (data: { symbolSize: number }) => data.symbolSize,
          itemStyle: {
            color: '#4046CA',
            borderColor: '#4046CA',
            borderWidth: 1,
            opacity: 1,
          },
          z: 3
        });
      }



    }

    // Calculate right padding based on secondary/tertiary legends on the right
    let rightPadding = 80; // Base padding
    if (showLegend && needsCategorization) {
      // Check if legends will be combined (same categorization for color and marker)
      const shouldCombineLegends = colorBy !== 'none' && markerBy !== 'none' && colorBy === markerBy;

      // Only add extra padding when secondary/tertiary legends will actually be on the right
      const hasSecondaryLegend = colorBy !== 'none' && markerBy !== 'none' && !shouldCombineLegends;
      const hasTertiaryLegend = (colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform';

      if (hasSecondaryLegend || hasTertiaryLegend) {
        rightPadding = 160; // Extra space for right-positioned legends
      }
    } else if (showLegend) {
      rightPadding = 80; // Basic padding for simple legends
    } else {
      rightPadding = 20; // Minimal padding when no legend
    }

    return {
      grid: {
        left: 60,
        right: rightPadding,
        top: 35,
        bottom: 60,
        containLabel: true,
      },
      xAxis: {
        type: 'log',
        name: 'Operation Time (years)',
        nameLocation: 'middle',
        nameGap: 25,
        nameTextStyle: {
          fontSize: 11,
          color: '#374151',
        },
        min: logMinBoundary,
        max: setMaxAxis(maxBoundary),
        axisLine: {
          onZero: false,
          lineStyle: {
            color: '#374151',
          },
        },
        position: 'bottom',
        offset: 0,
        logBase: 10,
        // Force logarithmic scale behavior with explicit tick configuration
        interval: 0, // Force to show all major ticks
        splitNumber: 8, // Reasonable number of major divisions
        axisLabel: {
          formatter: (value: number) => {
            // Enhanced formatter for logarithmic scale with better precision
            if (value < 1) return value.toFixed(1);
            if (value < 10) return value.toFixed(1);
            if (value < 100) return Math.round(value).toString();
            if (value < 1000) return Math.round(value).toString();
            return (value >= 1000 ? (value / 1000).toFixed(1) + 'k' : value.toFixed(0));
          },
          fontSize: 10,
          color: '#6b7280',
          rotate: 0, // Keep labels horizontal
          margin: 8,
        },
        axisTick: {
          show: true,
          inside: false,
          interval: 0, // Show all major ticks
          length: 6,
        },
        minorTick: {
          show: true,
          splitNumber: 9, // 9 minor ticks between major ticks for proper log scale
          length: 3,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#e0e7ff',
            type: 'dashed',
            width: 1,
          },
        },
        minorSplitLine: {
          show: true,
          lineStyle: {
            color: '#f3f4f6',
            type: 'solid',
            width: 0.5,
          },
        },
      },
      yAxis: {
        type: 'value',
        name: 'Failure Probability (%)',
        nameLocation: 'middle',
        nameGap: 35,
        min: weibullY(0.001),
        max: weibullY(0.9999),
        axisLabel: {
          customValues: customYValues,
          formatter: (value: number) => {
            const prob = inverseWeibullY(value) * 100;
            let closest = probTicks[0];
            let minDiff = Math.abs(prob - probTicks[0]);
            for (const tick of probTicks) {
              const diff = Math.abs(prob - tick);
              if (diff < minDiff) {
                minDiff = diff;
                closest = tick;
              }
            }
            return `${closest}%`;
          },
        },
      },
      legend: showLegend ? (() => {
        if (needsCategorization) {
          // Check if colorBy and markerBy are the same to combine legends
          const shouldCombineLegends = colorBy !== 'none' && markerBy !== 'none' && colorBy === markerBy;

          // Create multiple legends for different customizations
          const legends = [];

          // Primary Legend: Always show the main categorization (color > marker > size priority)
          if (colorBy !== 'none') {
            // Color-based series - primary legend
            const colorLegendData = [];
            colorCategories.forEach((category, index) => {
              const color = COLOR_PALETTES.category20[index % COLOR_PALETTES.category20.length];
              colorLegendData.push({
                name: category,
                icon: shouldCombineLegends ? MARKER_SHAPES[index % MARKER_SHAPES.length] : 'circle',
                itemStyle: {
                  color: color,
                  borderColor: color,
                  borderWidth: 1
                },
                textStyle: { color: '#374151', fontWeight: 'normal', lineHeight: 20, padding: [2, 0, 2, 4] }
              });
            });

            legends.push({
              data: colorLegendData,
              type: 'scroll',
              orient: 'horizontal',
              bottom: 5,
              left: 'center',
              selector: false,
              itemWidth: 18,
              itemHeight: 14,
              itemGap: 15,
              scrollDataIndex: 0,
              pageButtonItemGap: 5,
              pageButtonGap: 10,
              pageIconSize: 12,
              pageTextStyle: { fontSize: 10 }
            });
          } else if (markerBy !== 'none') {
            // Marker-based series - primary legend
            const markerLegendData = [];
            markerCategories.forEach((category, index) => {
              markerLegendData.push({
                name: category,
                icon: MARKER_SHAPES[index % MARKER_SHAPES.length],
                textStyle: { color: '#374151', fontWeight: 'normal', lineHeight: 20, padding: [2, 0, 2, 4] }
              });
            });

            legends.push({
              data: markerLegendData,
              type: 'scroll',
              orient: 'horizontal',
              bottom: 5,
              left: 'center',
              selector: false,
              itemWidth: 18,
              itemHeight: 14,
              itemGap: 15,
              scrollDataIndex: 0,
              pageButtonItemGap: 5,
              pageButtonGap: 10,
              pageIconSize: 12,
              pageTextStyle: { fontSize: 10 }
            });
          } else if (sizeBy !== 'uniform') {
            // Size-based series - primary legend
            const sizeData = getSizeLegendData(sizeBy);
            const sizeLegendData = [];
            sizeData.forEach(item => {
              sizeLegendData.push({
                name: item.name,
                icon: 'circle',
                textStyle: { color: '#374151', fontWeight: 'normal', lineHeight: 20, padding: [2, 0, 2, 4] }
              });
            });

            legends.push({
              data: sizeLegendData,
              type: 'scroll',
              orient: 'horizontal',
              bottom: 5,
              left: 'center',
              selector: false,
              itemWidth: 18,
              itemHeight: 14,
              itemGap: 15,
              scrollDataIndex: 0,
              pageButtonItemGap: 5,
              pageButtonGap: 10,
              pageIconSize: 12,
              pageTextStyle: { fontSize: 10 }
            });
          }

          // Secondary Legend: Show marker information when color is primary and not combined
          if (colorBy !== 'none' && markerBy !== 'none' && !shouldCombineLegends) {
            const markerLegendData = [];
            markerCategories.forEach((category, index) => {
              markerLegendData.push({
                name: category, // Remove prefix like "Component:", "Turbine:", etc.
                icon: MARKER_SHAPES[index % MARKER_SHAPES.length],
                itemStyle: {
                  color: '#6b7280',
                  borderColor: '#6b7280',
                  borderWidth: 1
                }, // Single color for all marker legend items
                textStyle: { color: '#6b7280', fontSize: 11, fontWeight: 'normal' }
              });
            });

            legends.push({
              data: markerLegendData,
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: '20%',
              selector: false,
              itemWidth: 16,
              itemHeight: 12,
              itemGap: 8,
              scrollDataIndex: 0,
              pageButtonItemGap: 4,
              pageButtonGap: 8,
              pageIconSize: 10,
              pageTextStyle: { fontSize: 9 },
              textStyle: { fontSize: 11, color: '#6b7280' }
            });
          }

          // Tertiary Legend: Show size information when not primary
          if ((colorBy !== 'none' || markerBy !== 'none') && sizeBy !== 'uniform') {
            const sizeData = getSizeLegendData(sizeBy);
            const tertiaryLegendData = [];
            sizeData.forEach(item => {
              tertiaryLegendData.push({
                name: item.name, // Remove prefix like "Size:"
                icon: 'circle',
                itemStyle: {
                  color: '#9ca3af',
                  borderColor: '#9ca3af',
                  borderWidth: 1
                }, // Single color for all size legend items
                textStyle: { color: '#6b7280', fontSize: 10, fontWeight: 'normal' }
              });
            });

            legends.push({
              data: tertiaryLegendData,
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: '60%',
              selector: false,
              itemWidth: 14,
              itemHeight: 10,
              itemGap: 6,
              scrollDataIndex: 0,
              pageButtonItemGap: 3,
              pageButtonGap: 6,
              pageIconSize: 8,
              pageTextStyle: { fontSize: 8 },
              textStyle: { fontSize: 10, color: '#6b7280' }
            });
          }

          return legends;
        } else {
          // Basic series legend
          return {
            type: 'scroll',
            orient: 'horizontal',
            bottom: 5,
            left: 'center',
            itemGap: 15,
            data: series
              .filter(s => s.name && !['Lower CI', 'Upper CI', 'Confidence Band'].includes(s.name as string))
              .map(s => s.name as string),
          };
        }
      })() : undefined,
      tooltip: {
        trigger: 'item',
        // Keep the tooltip inside the chart viewport so points high on the y-axis don't render a
        // card that overflows the top of the plot and gets clipped by the ancestor
        // `overflow-y-auto` scroll container.
        confine: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#d1d5db',
        borderWidth: 1,
        borderRadius: 8,
        padding: 0,
        formatter: (params: unknown) => {
          const tooltipParams = params as TooltipParams;
          if (tooltipParams.componentType === 'series' &&
            (tooltipParams.seriesName === 'Failure Data' ||
              (needsCategorization && tooltipParams.seriesName !== 'Weibull Fit' && tooltipParams.seriesName !== 'Confidence Band' && tooltipParams.seriesName !== 'Censored'))) {
            const groupedRecords = tooltipParams.data?.records;
            if ((tooltipParams.data?.count ?? 1) > 1 && groupedRecords && groupedRecords.length > 1) {
              return buildGroupedTooltip('failures', '#3b82f6', tooltipParams.data?.originalTime, groupedRecords, tooltipParams.data?.count ?? groupedRecords.length);
            }
            const record = tooltipParams.data?.record;
            if (!record) return null;

            return `
              <div style="background: white; padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); max-width: 24rem;">
                <div style="border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 8px;">
                  <p style="font-weight: 600; color: #111827; margin: 0;">Case #${esc(record.case_id)}</p>
                  <p style="font-size: 14px; color: #6b7280; margin: 4px 0 0 0;">${esc(record.turbine_id)} • ${esc(record.site_name)}</p>
                </div>
                <div style="font-size: 14px;">
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Operation Time:</span>
                    <span style="font-weight: 500;">${tooltipParams.data?.originalTime?.toFixed(2)} years</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Failure Probability:</span>
                    <span style="font-weight: 500; color: #3b82f6;">${tooltipParams.data?.originalProbability?.toFixed(1)}%</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Component:</span>
                    <span style="font-weight: 500;">${esc(record.component_major)}</span>
                  </div>
                  ${record.component_minor
                ? `
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Sub-component:</span>
                    <span style="font-weight: 500;">${esc(record.component_minor)}</span>
                  </div>
                  `
                : ''
              }
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Failure Mode:</span>
                    <span style="font-weight: 500;">${esc(record.failure_mode || 'Unknown')}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Type:</span>
                    <span style="
                      display: inline-flex;
                      padding: 4px 8px;
                      font-size: 12px;
                      font-weight: 600;
                      border-radius: 9999px;
                      ${record.outcome_name.includes('Replace')
                ? 'background-color: #fee2e2; color: #dc2626;'
                : record.outcome_name.includes('Planned')
                  ? 'background-color: #dbeafe; color: #1e40af;'
                  : 'background-color: #fef3c7; color: #d97706;'
              }
                    ">
                      ${esc(record.outcome_name)}
                    </span>
                  </div>
                  <div style="display: flex; justify-content: space-between;">
                    <span style="color: #6b7280;">Estimated Shutdown Date:</span>
                    <span style="font-weight: 500;">${new Date(record.failure_date).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            `;
          }

          // Censored data tooltip
          if (tooltipParams.seriesName === 'Censored') {
            const count = tooltipParams.data?.count || 1;
            const turbineText = count === 1 ? 'turbine' : 'turbines';

            return `
              <div style="background: white; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);">
                <div style="font-size: 14px;">
                  <div style="font-weight: 600; color: #111827; margin-bottom: 4px;">Censored Observations</div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Operation Time:</span>
                    <span style="font-weight: 500; margin-left: 16px;">${tooltipParams.data?.originalTime?.toFixed(2)} years</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Number of ${turbineText}:</span>
                    <span style="font-weight: 500; margin-left: 16px;">${count}</span>
                  </div>
                  <div style="font-size: 12px; color: #9ca3af; margin-top: 4px;">Still operational</div>
                </div>
              </div>
            `;
          }

          if (tooltipParams.seriesName === 'ONYX Failure') {
            const groupedRecords = tooltipParams.data?.records;
            if ((tooltipParams.data?.count ?? 1) > 1 && groupedRecords && groupedRecords.length > 1) {
              return buildOnyxGroupedTooltip(tooltipParams.data?.originalTime, groupedRecords, tooltipParams.data?.count ?? groupedRecords.length);
            }
            const record = tooltipParams.data?.record;
            if (!record) return null;
            return `<div style="background: white; padding: 16px; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); max-width: 24rem;">
                <div style="border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; margin-bottom: 8px;">
                  <p style="font-weight: 600; color: #111827; margin: 0;">ONYX Failure Observation</p>
                </div>
                <div style="font-size: 14px;">
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Operation Time:</span>
                    <span style="font-weight: 500;">${tooltipParams.data?.originalTime?.toFixed(2)} years</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Failure Probability:</span>
                    <span style="font-weight: 500; color: #3b82f6;">${tooltipParams.data?.originalProbability?.toFixed(1)}%</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Component:</span>
                    <span style="font-weight: 500;">${esc(record.component_major)}</span>
                  </div>
                  ${record.component_minor
                ? `
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Sub-component:</span>
                    <span style="font-weight: 500;">${esc(record.component_minor)}</span>
                  </div>
                  `
                : ''
              }
                </div>
              </div>
            `;
          }

          if (tooltipParams.seriesName === 'ONYX Censor') {
            const count = tooltipParams.data?.count || 1;
            const componentText = count === 1 ? 'component' : 'components';
            return `
              <div style="background: white; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);">
                <div style="font-size: 14px;">
                  <div style="font-weight: 600; color: #111827; margin-bottom: 4px;">ONYX Censor Observation</div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Operation Time:</span>
                    <span style="font-weight: 500; margin-left: 16px;">${tooltipParams.data?.originalTime?.toFixed(2)} years</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Number of ${componentText}:</span>
                    <span style="font-weight: 500; margin-left: 16px;">${count}</span>
                  </div>
                </div>
              </div>
            `;
          }

          return null;
        },
      },
      toolbox: {
        feature: {
          dataZoom: {
            yAxisIndex: false,
            title: {
              zoom: 'Zoom',
              back: 'Reset',
            },
          },
          restore: {
            title: 'Reset',
          },
          saveAsImage: {
            title: 'Save',
            pixelRatio: 2,
          },
        },
        right: rightPadding,
        top: 10,
      },
      series,
    };
  }, [analysis, showConfidenceIntervals, showCensoredData, censoredData, chartType, colorBy, markerBy, sizeBy, showLegend, onyxCensorData, showOnyxData, groupTiedTimes]);
};
